import { inject, Injectable } from '@angular/core';
import type { CustomerModel } from '@libs/account/models';
import type { DocumentCountsModel, DocumentModel } from '@libs/services/models';
import { AuthService } from '@libs/shared/auth';
import { FileModel } from '@libs/shared/models';
import { DocumentsSummary, FileSaverService } from '@libs/shared/services';
import { distinctUntilChanged, firstValueFrom, map, Observable, of, shareReplay, switchMap } from 'rxjs';
import { DocumentRepositoryService } from './document-repository.service';

@Injectable({ providedIn: 'root' })
export class DocumentService implements DocumentsSummary {
  private readonly authService: AuthService = inject(AuthService);
  private readonly repository: DocumentRepositoryService = inject(DocumentRepositoryService);
  private readonly fileSaverService: FileSaverService = inject(FileSaverService);

  private readonly documents$: Observable<DocumentModel[]> = this.sharePerCustomer(() => this.repository.getDocuments(), []);
  private readonly documentCounts$: Observable<DocumentCountsModel> = this.sharePerCustomer(
    () => this.repository.getDocumentCounts(),
    { total: 0, noLabel: 0, labels: {} },
  );

  public getDocuments(): Observable<DocumentModel[]> {
    return this.documents$;
  }

  public getDocumentCounts(): Observable<DocumentCountsModel> {
    return this.documentCounts$;
  }

  public async downloadDocument(fileIdentifier: string): Promise<void> {
    const file: FileModel = await firstValueFrom(this.repository.getDocumentFile(fileIdentifier));

    await this.fileSaverService.saveBlob(file.blob, file.filename);
  }

  private sharePerCustomer<T>(request: () => Observable<T>, withoutCustomer: T): Observable<T> {
    return this.authService.getCurrentCustomer().pipe(
      map((customer: CustomerModel | undefined) => customer?.id),
      distinctUntilChanged(),
      switchMap((customerId: string | undefined) => (customerId === undefined ? of(withoutCustomer) : request())),
      shareReplay(1),
    );
  }
}
